/**
 * Returns `value` only when it is a safe, navigable URL (http/https), otherwise `undefined`.
 *
 * Any string that is rendered as a link or passed to `window.open()` / an anchor `href` MUST be
 * run through this guard first when its origin is user-controlled. Conversation tags such as
 * `pr-filed` are set via the public `tagConversation` GraphQL mutation and are therefore fully
 * attacker-controlled, so a value like `javascript:fetch('/api/v2/tokens', ...)` would otherwise
 * execute in the app.launchdarkly.com origin when a victim clicks the "PR opened" button.
 *
 * The check parses the value with the URL constructor and allow-lists the `http:` and `https:`
 * protocols. This rejects dangerous schemes (`javascript:`, `data:`, `vbscript:`, `file:`, ...)
 * regardless of casing, embedded whitespace/control characters, or leading garbage that browsers
 * would otherwise ignore when interpreting the scheme.
 *
 * See SEC-8216.
 */
export function getSafeHttpUrl(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  let parsed: URL;
  try {
    // Resolve against the current origin so relative paths stay supported; absolute URLs keep
    // their own scheme, which is what the allow-list below inspects.
    parsed = new URL(value, window.location.origin);
  } catch {
    return undefined;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return undefined;
  }

  return parsed.toString();
}

/**
 * Destinations the agent is allowed to link a user to.
 *
 * Agent-authored content — both tool results relayed through `storeAgentMessage` and the
 * free-form markdown of the agent's own response — is attacker-influenceable: a conversation
 * author can forge messages and then share the conversation. `getSafeHttpUrl` blocks dangerous
 * schemes, but it would still let the agent link a victim to an arbitrary http(s) site. This
 * host allow-list narrows navigable external destinations to trusted ones.
 *
 * Same-origin / relative links are always allowed (they cannot exfiltrate to a third party), so
 * this list only needs to enumerate trusted *external* origins.
 */
export const AGENT_CONTENT_ALLOWED_HOSTS = [
  'github.com',
  'launchdarkly.com',
  'app.launchdarkly.com',
  'app.ld.catamorphic.com',
  'ld-stg.launchdarkly.com',
];

function isAllowedAgentContentHost(hostname: string): boolean {
  return AGENT_CONTENT_ALLOWED_HOSTS.some((allowed) => hostname === allowed || hostname.endsWith(`.${allowed}`));
}

/**
 * Returns `value` only when it is a safe, navigable URL (http/https scheme) that points at either
 * the current origin or an {@link AGENT_CONTENT_ALLOWED_HOSTS allow-listed} external host. Returns
 * `undefined` otherwise. Use this for any URL that originates from agent content before it reaches
 * a navigation sink (`window.open`, anchor `href`). Treat `undefined` as "render inert".
 */
export function getAllowedAgentContentUrl(value: string | null | undefined): string | undefined {
  const safe = getSafeHttpUrl(value);
  if (!safe) {
    return undefined;
  }

  // `safe` is already an absolute URL resolved against the current origin, so this never throws.
  const parsed = new URL(safe);
  if (parsed.origin === window.location.origin) {
    return safe;
  }

  return isAllowedAgentContentHost(parsed.hostname) ? safe : undefined;
}

/**
 * Stable predicate form of {@link getAllowedAgentContentUrl} suitable for passing to components
 * (e.g. the shared MarkdownRenderer) without allocating a new function on every render.
 */
export function isAllowedAgentContentUrl(value: string | null | undefined): boolean {
  return getAllowedAgentContentUrl(value) !== undefined;
}
